import { EFinancingProgram } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { PageContainer, useIfUser } from '@if/mui'
import { useCreditApplicationByIdQuery } from '@src/data/api/credit-api/credit-api'
import { useMerchantById, useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import CreateWorksheetPage from '.'
import WorksheetSkeleton from './components/WorksheetSkeleton'

const WorksheetContent = ({ creditApplication }: { creditApplication: CreditApplication }) => {
  const user = useIfUser()
  const [merchant, isLoadingMerchant] = useMerchantById(creditApplication.merchantId)
  const [merchantsListFromParentId, isFetchingMerchantsList] = useMerchantByParentId(user?.merchantId ?? '')
  const isLoadingInfo = isLoadingMerchant || isFetchingMerchantsList

  if (isLoadingInfo) return <WorksheetSkeleton />
  if (!user || !merchant || !merchantsListFromParentId) return <Navigate to="/" />

  return (
    <CreateWorksheetPage
      creditApplication={creditApplication}
      merchant={merchant}
      user={user}
      merchantsListFromParentId={merchantsListFromParentId}
    />
  )
}

const worksheetParams = ['financingProgramId', 'id'] as const

const useWorksheetPrimary = ({ financingProgramId, id }: { financingProgramId: string; id: string }) => ({
  creditApplication: useCreditApplicationByIdQuery(
    { creditApplicationId: id, financingProgramId: financingProgramId as EFinancingProgram },
    false,
  ),
})

const WorksheetWrapper = () => {
  const { t } = useTranslation()
  const { financingProgramId, id } = useParams()
  return (
    <PageContainer
      params={worksheetParams}
      usePrimary={useWorksheetPrimary}
      loadingContent={<WorksheetSkeleton />}
      back={{ to: `/credits/${financingProgramId}/${id}` }}
      title={({ creditApplication }) =>
        creditApplication.financingProgramId === EFinancingProgram.MechanicalWarranty
          ? t('mechanicalWarrantyWorksheet.title')
          : t('worksheet.worksheet')
      }
      subtitle={({ creditApplication }) =>
        creditApplication.financingProgramId === EFinancingProgram.Personal
          ? `${t('worksheet.maxLoanAmount')}: ${formatCurrency(creditApplication.finalCreditDecision.maxAmountFinanced)}`
          : ''
      }
    >
      {({ creditApplication }) => <WorksheetContent creditApplication={creditApplication} />}
    </PageContainer>
  )
}
export default WorksheetWrapper
